import React, { forwardRef } from "react"
import cx from "classnames"
import {
  ButtonComponentProps,
  ButtonLinkComponentProps,
  ButtonProps,
  isLinkProps,
} from "./type"
import { ButtonLinkBase, ButtonBase } from "./ButtonComponent"
import { ChevronRightIcon } from "../../Icon"

const getComputedClassName = ({
  className,
  variant,
  size,
  destructive,
  primary,
  text,
  isIconOnly,
  fullWidth,
}: {
  className?: ButtonProps["className"]
  variant?: ButtonProps["variant"]
  size?: ButtonProps["size"]
  destructive?: ButtonProps["destructive"]
  primary?: ButtonProps["primary"]
  text?: ButtonProps["text"]
  isIconOnly?: boolean
  fullWidth?: ButtonProps["fullWidth"]
}) => {
  return cx(className, "cobalt-button", {
    "cobalt-button--primary": primary,
    "cobalt-button--destructive": destructive,
    "cobalt-button--text": text,
    "cobalt-button--icon-only": isIconOnly,
    "cobalt-button--small": size === "small",
    "cobalt-button--large": size === "large",
    "cobalt-button--warning": variant === "warning",
    "cobalt-button--secondary": variant === "secondary",
    "cobalt-button--tertiary": variant === "tertiary",
    "cobalt-button--business": variant === "business",
    "cobalt-button--success": variant === "success",
    "cobalt-button--neutral": variant === "neutral",
    "c-w-full": fullWidth,
  })
}

interface ButtonOverloads {
  (
    props: Exclude<ButtonProps, ButtonComponentProps> &
      React.RefAttributes<HTMLAnchorElement>
  ): React.ReactElement
  (
    props: Exclude<ButtonProps, ButtonLinkComponentProps> &
      React.RefAttributes<HTMLButtonElement>
  ): React.ReactElement
  displayName?: string
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (props, ref) => {
    const {
      children,
      className,
      variant,
      size,
      destructive,
      primary,
      text,
      icon,
      fullWidth,
      ...restProps
    } = props

    const computedClassName = getComputedClassName({
      className,
      variant,
      size,
      destructive,
      primary,
      text,
      fullWidth,
      isIconOnly: !!icon && !children,
    })

    const content = (
      <>
        {icon}
        {children && <span>{children}</span>}
        {text && children ? <ChevronRightIcon /> : null}
      </>
    )

    if (isLinkProps(restProps)) {
      return (
        <ButtonLinkBase
          {...(restProps as ButtonLinkComponentProps)}
          className={computedClassName}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        >
          {content}
        </ButtonLinkBase>
      )
    }

    return (
      <ButtonBase
        {...(restProps as ButtonComponentProps)}
        className={computedClassName}
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
      >
        {content}
      </ButtonBase>
    )
  }
) as ButtonOverloads

Button.displayName = "Button"

export default Button
